import dayjs from "dayjs"
import weekday from "dayjs/plugin/weekday"
import isBetween from "dayjs/plugin/isBetween"
dayjs.extend(weekday, isBetween)

export const scrollToElement = (elementId, withHighlight = true) => {
  requestAnimationFrame(() => {
    const targetElm = document.getElementById(elementId)

    if (!targetElm) {
      return
    }

    targetElm?.scrollIntoView({ behavior: "smooth", block: "center" })

    if (withHighlight) {
      // Add a blinking class
      targetElm.classList.add("highlight")
      // Remove the class after 1 second
      setTimeout(() => {
        targetElm.classList.remove("highlight")
      }, 2000)
    }
  })
}

export const getPayAdjustment = (employee) => {
  return employee?.rates?.weekly_rate_w2 ? "Weekly" : "Daily"
}

export const isDayInWeek = (day, week) => {
  const startWeek = dayjs(week, "YYYY-MM-DD").startOf("day")
  const endWeek = startWeek.add(6, "day").endOf("day")

  return dayjs(day, "YYYY-MM-DD").startOf("day").isBetween(startWeek, endWeek, "day", "[]")
}

export const getDayFromWeek = (currentWeek) => {
  const start = dayjs(currentWeek, "YYYY-MM-DD", true).startOf("day")
  const end   = start.add(6, "day").endOf("day")
  const today = dayjs()

  return today.isBetween(start, end, "day", "[]")
      ? today.startOf("day")
      : start
}

export const getStartOfWeekFromToday = () => {
  const today = dayjs()
  const weekday = today.day()

  const number = (weekday === 6 ? 0 : weekday + 1)

  return today.subtract(number, "day").format("YYYY-MM-DD")
}

export const getThisWeek = () => {
  return dayjs().weekday(6).subtract(2, "week").format("YYYY-MM-DD")
}

export const getThisWeekWithWednesdayShift = () => {
  const curDayToInWeek = dayjs().day()

  if (curDayToInWeek < 3) {
    return dayjs().weekday(6).subtract(3, "week").format("YYYY-MM-DD")
  } else {
    return dayjs().weekday(6).subtract(2, "week").format("YYYY-MM-DD")
  }
}

export const getPreviousWeek = () => {
  const curDayToInWeek = dayjs().day();

  if (curDayToInWeek < 3) {
    return dayjs().weekday(6).subtract(4, "week").format("YYYY-MM-DD");
  } else {
    return dayjs().weekday(6).subtract(3, "week").format("YYYY-MM-DD");
  }
}

export const getEndOfWeekFromStart = (startOfWeek) => {
  return dayjs(startOfWeek).add(6, 'day').toDate();
}

export const getPayrollPeriodString = (periodStartDate) => {
  if (!periodStartDate) return ""
  return `${dayjs(periodStartDate).format("MM/DD/YYYY")} - ${dayjs(periodStartDate)
    .add(6, "d")
    .format("MM/DD/YYYY")}`
}

export const formatDate = (date, withYear = true, daysToAdd = 0) => {
  const dayjsDate = dayjs(date)

  let format = "MMM DD"

  if (withYear) {
    format = "MMM DD, YYYY"
  }

  if (daysToAdd > 0) {
    return dayjsDate.add(daysToAdd, "d").format(format)
  }

  return dayjsDate.format(format)
}

export const initiateFileDownloadFromBlob = (blob, fileName) => {
  // Create a URL for the Blob object.
  const fileURL = window.URL.createObjectURL(blob)

  // Get the extension file
  let extension = blob.type.split("/")[1]

  if (extension === "plain") {
    extension = "csv";
  }

  // Create a temporary download link element.
  const downloadLink = document?.createElement("a")
  downloadLink.href = fileURL
  downloadLink.setAttribute("download", `${fileName}.${extension}`) // Set the download file name.

  // Append the link to the document and trigger the download.
  document?.body.appendChild(downloadLink)
  downloadLink.click()

  // Clean up by removing the link and revoking the created URL.
  document?.body.removeChild(downloadLink)
  window.URL.revokeObjectURL(fileURL)
}

export const isUserSourceRestricted = () => {
  const userData = JSON.parse(localStorage.getItem("user"))

  const restrictedSources = ["package_route"]

  if (userData && userData.organization) {
    return restrictedSources.includes(userData.organization.source)
  }

  return false
}

export const getUserSource = () => {
  const userData = JSON.parse(localStorage.getItem("user"))

  if (userData && userData.organization) {
    return userData.organization.source
  }

  return ""
}

export const isUserPackageRoute = () => {
  return getUserSource() === "package_route"
}

export const defaultSettingPage = () => {
  // companies for user source restricted
  if (isUserSourceRestricted()) {
    return "companies"
  }

  // default page for other users
  return "profile"
}

export const redirectUserSourceRestricted = (currentPage, setPage, nav) => {
  const restrictedPages = ["profile", "subscription"]
  if (isUserSourceRestricted() && restrictedPages.includes(currentPage)) {
    setPage("companies")

    nav({
      pathname: "/settings",
      search: `?page=companies`
    })
  }
}

export const calculatePercentageOfTotal = (partValue, totalValue) => {
  return (partValue && totalValue ? (partValue / totalValue) * 100 : 0).toFixed(2)
}

export const transformBytesInToKilobytes = (bytes = 0) => {
  return (bytes / 1024).toFixed(1)
}

export const calculateTotalPayConsideringCapReductionBonus = (employeeRates, calculatedHour, total_w2, added_bonus, added_deduction_bonus, six_day_bonus, sunday_bonus, totalPay) => {
  if (!employeeRates?.is_cap_reduction_bonus_enabled) {
    if (calculatedHour > 0 && !total_w2) {
      const totalBonus = Number(added_bonus) + Number(six_day_bonus) + Number(sunday_bonus)

      return (Number(totalPay) + totalBonus) - Number(added_deduction_bonus)
    }
  }

  return totalPay
}

export const getPayrollStepNameByType = (type) => {
  switch (type) {
    case "dsw":
      return "DSW"
    case "home-base":
      return "Home Base"
    default:
      return null
  }
}


export const truncateText = (text, maxLength = 50) => text?.length > maxLength ? `${text.slice(0, maxLength)}...` : text;

export const normalizerText = (s) => (s || "").toString().toLowerCase().trim()

// hex over optional background (default: white). If blendWith is null,
// it falls back to returning #RRGGBBAA like your original.
export const withAlphaHex = (hex, a = 0.3, blendWith = '#ffffff') => {
  if (!hex?.startsWith('#')) return hex; // non-hex: return as-is

  // normalize foreground hex -> RRGGBB
  let h = hex.slice(1);
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.slice(0,2), 16);
  const g = parseInt(h.slice(2,4), 16);
  const b = parseInt(h.slice(4,6), 16);

  // If no background mixing requested, return transparent hex (#RRGGBBAA)
  if (!blendWith) {
    const alpha = Math.round(Math.max(0, Math.min(1, a)) * 255)
    .toString(16).padStart(2, '0');
    return `#${h}${alpha}`;
  }

  // normalize background hex
  let bh = blendWith.replace('#','');
  if (bh.length === 3) bh = bh.split('').map(c => c + c).join('');
  const br = parseInt(bh.slice(0,2), 16);
  const bg = parseInt(bh.slice(2,4), 16);
  const bb = parseInt(bh.slice(4,6), 16);

  // composite: result = fg*a + bg*(1-a)
  const A = Math.max(0, Math.min(1, a));
  const R = Math.round(r * A + br * (1 - A));
  const G = Math.round(g * A + bg * (1 - A));
  const B = Math.round(b * A + bb * (1 - A));

  const toHex2 = v => v.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex2(R)}${toHex2(G)}${toHex2(B)}`; // opaque hex
};


export const isBeforeFromDay = (day) => {
  return dayjs(day).isBefore(dayjs(), "day")
}
